<script lang="ts">
	import { onMount } from 'svelte';
	import { fade, scale } from 'svelte/transition';
	import { cubicOut } from 'svelte/easing';
	import * as m from '$lib/paraglide/messages';

	let { ariaLabel = m.loading_in_progress() } = $props();

	let progress = $state(0);
	let animationFrame: number;

	onMount(() => {
		const animate = () => {
			progress = Math.min(progress + (100 - progress) * 0.08, 95);

			if (progress < 95) {
				animationFrame = requestAnimationFrame(animate);
			}
		};

		animationFrame = requestAnimationFrame(animate);

		return () => {
			if (animationFrame) {
				cancelAnimationFrame(animationFrame);
			}
		};
	});
</script>

<div
	class="loader-overlay"
	role="alert"
	aria-live="polite"
	aria-label={ariaLabel}
	transition:fade={{ duration: 300, easing: cubicOut }}
>
	<div class="loader-content">
		<!-- Logo avec animation de pulsation -->
		<div class="logo-container" transition:scale={{ duration: 400, easing: cubicOut }}>
			<div class="logo-wrapper">
				<svg class="logo-circle" viewBox="0 0 120 120" aria-hidden="true">
					<circle class="circle-track" cx="60" cy="60" r="54" fill="none" stroke-width="4" />
					<circle
						class="circle-progress"
						cx="60"
						cy="60"
						r="54"
						fill="none"
						stroke-width="4"
						stroke-linecap="round"
						style="--progress: {progress}"
					/>
				</svg>
			</div>
		</div>

		<!-- Texte de chargement -->
		<div class="loader-text">
			<p class="loading-message">{m.loading_in_progress()}</p>
			<div class="loading-dots">
				<span class="dot"></span>
				<span class="dot"></span>
				<span class="dot"></span>
			</div>
		</div>
	</div>

	<!-- Barre de progression en bas -->
	<div class="progress-bar-container">
		<div
			class="progress-bar"
			role="progressbar"
			aria-valuenow={Math.round(progress)}
			aria-valuemin={0}
			aria-valuemax={100}
			style="width: {progress}%"
		></div>
	</div>
</div>

<style>
	.loader-overlay {
		position: fixed;
		inset: 0;
		z-index: 9999;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		background: linear-gradient(135deg, hsl(var(--background)) 0%, hsl(var(--muted) / 0.3) 100%);
		backdrop-filter: blur(10px);
	}

	.loader-content {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 3rem;
	}

	.logo-container {
		position: relative;
		animation: float 3s ease-in-out infinite;
	}

	@keyframes float {
		0%,
		100% {
			transform: translateY(0px);
		}
		50% {
			transform: translateY(-10px);
		}
	}

	.logo-wrapper {
		position: relative;
		width: 120px;
		height: 120px;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.logo-circle {
		position: absolute;
		inset: 0;
		width: 120px;
		height: 120px;
		transform: rotate(-90deg);
	}

	.circle-track {
		stroke: hsl(var(--muted));
		opacity: 0.2;
	}

	.circle-progress {
		stroke: hsl(var(--primary));
		stroke-dasharray: 339; /* 2 * PI * 54 */
		stroke-dashoffset: calc(339 - (339 * var(--progress) / 100));
		transition: stroke-dashoffset 0.3s cubic-bezier(0.4, 0, 0.2, 1);
		filter: drop-shadow(0 0 8px hsl(var(--primary) / 0.5));
	}

	.loader-text {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 1rem;
		text-align: center;
	}

	.loading-message {
		font-size: 1.125rem;
		font-weight: 500;
		color: hsl(var(--foreground));
		margin: 0;
		letter-spacing: 0.025em;
	}

	.loading-dots {
		display: flex;
		gap: 0.5rem;
		align-items: center;
	}

	.dot {
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background-color: hsl(var(--primary));
		animation: bounce 1.4s infinite ease-in-out both;
	}

	.dot:nth-child(1) {
		animation-delay: -0.32s;
	}

	.dot:nth-child(2) {
		animation-delay: -0.16s;
	}

	@keyframes bounce {
		0%,
		80%,
		100% {
			transform: scale(0);
			opacity: 0.5;
		}
		40% {
			transform: scale(1);
			opacity: 1;
		}
	}

	.progress-bar-container {
		position: absolute;
		bottom: 0;
		left: 0;
		right: 0;
		height: 4px;
		background-color: hsl(var(--muted) / 0.3);
		overflow: hidden;
	}

	.progress-bar {
		height: 100%;
		background: linear-gradient(
			90deg,
			hsl(var(--primary)) 0%,
			hsl(var(--primary) / 0.6) 50%,
			hsl(var(--primary)) 100%
		);
		background-size: 200% 100%;
		animation: shimmer 2s infinite;
		transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
		box-shadow:
			0 0 20px hsl(var(--primary) / 0.5),
			0 0 40px hsl(var(--primary) / 0.3);
	}

	@keyframes shimmer {
		0% {
			background-position: 200% 0;
		}
		100% {
			background-position: -200% 0;
		}
	}

	/* Mode sombre */
	@media (prefers-color-scheme: dark) {
		.loader-overlay {
			background: linear-gradient(135deg, hsl(var(--background)) 0%, hsl(var(--muted) / 0.2) 100%);
		}
	}

	/* Accessibilité */
	@media (prefers-reduced-motion: reduce) {
		.logo-container,
		.dot,
		.progress-bar {
			animation: none;
		}

		.circle-progress {
			transition: none;
		}
	}

	/* Responsive */
	@media (max-width: 640px) {
		.logo-wrapper {
			width: 100px;
			height: 100px;
		}

		.logo-circle {
			width: 100px;
			height: 100px;
		}

		.loading-message {
			font-size: 1rem;
		}
	}
</style>
